import React from 'react';

export type ProgressColor = 'primary' | 'success' | 'warning' | 'error' | 'accent';

export interface ProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  value: number; // 0 to 100
  max?: number;
  color?: ProgressColor;
  size?: 'sm' | 'md' | 'lg';
  showValue?: boolean;
  label?: string;
}

const progressColorStyles: Record<ProgressColor, string> = {
  primary: 'bg-[#1fb4ac]',
  success: 'bg-[#20A66A]',
  warning: 'bg-[#D4A923]',
  error: 'bg-[#D94A5C]',
  accent: 'bg-[#56E8E1]',
};

const progressSizeStyles = {
  sm: 'h-1.5',
  md: 'h-2.5',
  lg: 'h-4',
};

export const Progress: React.FC<ProgressProps> = ({
  value,
  max = 100,
  color = 'primary',
  size = 'md',
  showValue = false,
  label,
  className = '',
  ...props
}) => {
  const percentage = Math.min(Math.max(0, Math.round((value / max) * 100)), 100);

  return (
    <div className={`w-full space-y-1.5 ${className}`} {...props}>
      {(label || showValue) && (
        <div className="flex items-center justify-between text-xs font-semibold text-[#111111]">
          {label && <span>{label}</span>}
          {showValue && <span className="text-[#5F6368] font-bold">{percentage}%</span>}
        </div>
      )}
      <div className={`w-full bg-[#E5E9E9] rounded-full overflow-hidden ${progressSizeStyles[size]}`}>
        <div
          className={`h-full transition-all duration-300 rounded-full ${progressColorStyles[color]}`}
          style={{ width: `${percentage}%` }}
          role="progressbar"
          aria-valuenow={value}
          aria-valuemin={0}
          aria-valuemax={max}
        />
      </div>
    </div>
  );
};

export interface DividerProps extends React.HTMLAttributes<HTMLDivElement> {
  orientation?: 'horizontal' | 'vertical';
  label?: string;
}

export const Divider: React.FC<DividerProps> = ({
  orientation = 'horizontal',
  label,
  className = '',
  ...props
}) => {
  if (orientation === 'vertical') {
    return <div className={`w-[1px] bg-[#E5E9E9] self-stretch ${className}`} {...props} />;
  }

  if (label) {
    return (
      <div className={`flex items-center gap-3 my-4 ${className}`} {...props}>
        <div className="flex-1 h-[1px] bg-[#E5E9E9]" />
        <span className="text-[11px] font-bold text-[#5F6368] uppercase tracking-wider">{label}</span>
        <div className="flex-1 h-[1px] bg-[#E5E9E9]" />
      </div>
    );
  }

  return <hr className={`border-0 h-[1px] bg-[#E5E9E9] my-4 ${className}`} {...props} />;
};
